import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import {
  ArrowLeft,
  ArrowRight,
  Users,
  X, 
  Database,
  DollarSign,
  Wrench,
  FileSearch,
  Settings2,
  
  // --- Iconos NUEVOS para esta página ---
  ShieldCheck,    // Para Hero, Seguridad
  Archive,        // Para Backup
  Server,         // Para Servidor
  RotateCw,       // Para Recuperación
  HardDrive,      // Para Discos
  Layers,         // Para Particiones
  Smartphone,     // Para Móvil
  FileTerminal,   // Para Recuperación de Archivos
  Copy,           // Para Clonación/Migración
  ShieldAlert,    // Para Problema (Ransomware)
  CheckSquare,    // Para Beneficios
  Cloud,          // Para Nube Híbrida

} from 'lucide-react';

import ContactForm from '../ContactForm';
import LanguageSwitcher from '../components/LanguageSwitcher';


// --- COMPONENTES DE PÁGINA (Reutilizados) ---

// Header
const PageHeader = ({ onOpenPopup, ctaText }) => (
  <header className="fixed top-0 w-full bg-white z-50 border-b border-gray-200">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex justify-between items-center">
      <a href="/" className="flex items-center">
        <img
          src="https://imgur.com/nYWIvWJ.png"
          alt="Nextcoders Logo"
          className="h-8"
          onError={(e) => { e.target.onerror = null; e.target.src="https://placehold.co/150x32/1f2937/ffffff?text=Nextcoders"; }}
        />
      </a>
      <div className="flex items-center gap-4">
        <LanguageSwitcher />
        <button
          onClick={onOpenPopup}
          className="bg-[#141414] text-white px-4 md:px-6 py-2 rounded-lg hover:bg-gray-800 transition text-sm font-semibold"
        >
          {ctaText}
        </button>
      </div>
    </div>
  </header>
);

// --- PÁGINA DE SISTEMAS DE BACKUP ---

const SistemasBackupPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  // Estado para controlar el popup
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  // Estado para la pestaña de solución activa
  const [activeSolution, setActiveSolution] = useState(0);

  const solutionsData = useMemo(() => {
    const icons = [RotateCw, Database, Layers, Wrench, Smartphone, FileTerminal, Copy];
    const items = t('backup.solutions.items', { returnObjects: true });
    return items.map((item, index) => ({
      ...item,
      icon: icons[index]
    }));
  }, [t]);

  const implementationSteps = useMemo(() => t('backup.implementation.steps', { returnObjects: true }), [t]);
  const licenseItems = useMemo(() => t('backup.implementation.license_items', { returnObjects: true }), [t]);

  // Función para abrir el popup
  const openPopup = () => {
    setIsPopupOpen(true);
    document.body.style.overflow = 'hidden';
  };

  // Función para cerrar el popup
  const closePopup = () => {
    setIsPopupOpen(false);
    document.body.style.overflow = 'auto';
  };

  // Componente para item de "Cómo Trabajamos" (NUEVO)
  const StepItem = ({ icon: Icon, title, text }) => (
    <div className="flex items-start gap-4">
      <span className="flex-shrink-0 flex items-center justify-center w-10 h-10 bg-gray-200 text-gray-700 rounded-lg">
        <Icon size={20} />
      </span>
      <div>
        <h5 className="font-semibold text-gray-800">{title}</h5>
        <p className="text-sm text-gray-600">{text}</p>
      </div>
    </div>
  );


  return (
    <div className="min-h-screen bg-transparent font-inter">
      {/* Popup Modal para Bitrix24 */}
      {isPopupOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-60 z-50 flex justify-center items-center p-4" onClick={(e) => { if (e.target === e.currentTarget) closePopup(); }}>
          <div className="bg-white rounded-2xl p-8 max-w-lg w-full relative transform transition-all duration-300 scale-100">
            <button
              onClick={closePopup}
              className="absolute top-4 right-4 text-gray-500 hover:text-gray-800 transition"
              aria-label="Cerrar popup"
            >
              <X size={24} />
            </button>
            <div className="text-center">
              <h3 className="text-2xl font-bold text-[#141414] mb-3">{t('common.diagnostic_request')}</h3>
              <p className="text-gray-600 mb-6">{t('common.diagnostic_desc')}</p>
              <ContactForm
                formData="inline/4/10oz6w"
                formId="4"
                scriptSrc="https://cdn.bitrix24.es/b24284919/crm/form/loader_4.js"
              />
            </div>
          </div>
        </div>
      )}
      
      <PageHeader onOpenPopup={openPopup} ctaText={t('common.free_diagnostic')} />

      {/* Contenido Principal */}
      <main className="pt-32 pb-20 px-4 sm:px-6">
        <div className="max-w-6xl mx-auto">
          
          {/* Botón Volver */}
          <button
            type="button"
            onClick={() => (window.history.length > 1 ? navigate(-1) : navigate('/'))}
            className="inline-flex items-center gap-2 text-gray-600 hover:text-[#141414] mb-8 transition font-medium"
          >
            <ArrowLeft size={16} />
            {t('common.back')}
          </button>

          {/* Encabezado Principal (Hero Section) */}
          <div className="text-center mb-20">
            <div className="inline-block bg-blue-100 text-blue-700 p-3 rounded-full mb-4">
              <ShieldCheck size={32} />
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-[#141414] mb-4">
              {t('backup.hero.title')}
            </h1>
            <p className="text-lg md:text-xl text-gray-600 max-w-3xl mx-auto mb-4">
              {t('backup.hero.subtitle')}
            </p>
            <p className="text-base text-gray-500 max-w-4xl mx-auto">
              {t('backup.hero.desc')}
            </p>
          </div>
          
          
          {/* --- INICIO NUEVO DISEÑO --- */}

          {/* Sección 1: Intro */}
          <section className="mb-20">
            <h2 className="text-3xl font-bold text-center text-[#141414] mb-4">{t('backup.intro.title')}</h2>
            <p className="text-lg text-gray-600 text-center max-w-3xl mx-auto">
              {t('backup.intro.desc')}
            </p>
          </section>

          {/* Sección 2: Soluciones Interactivas (NUEVO DISEÑO) */}
          <section className="mb-20">
            <div className="flex flex-col md:flex-row gap-8 lg:gap-12">
              
              {/* Columna de Navegación (Izquierda) */}
              <div className="md:w-1/3">
                <h3 className="text-xl font-semibold text-[#141414] mb-4">{t('backup.solutions.title')}</h3>
                <div className="space-y-2">
                  {solutionsData.map((solution, index) => (
                    <button
                      key={index}
                      onClick={() => setActiveSolution(index)}
                      className={`w-full flex items-center gap-3 p-3 rounded-lg text-left transition ${
                        activeSolution === index
                          ? 'bg-blue-100 text-blue-700 shadow'
                          : 'bg-gray-50 text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                      }`}
                    >
                      <solution.icon size={20} className={activeSolution === index ? 'text-blue-600' : 'text-gray-500'} />
                      <span className="font-medium">{solution.title}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Columna de Contenido (Derecha) */}
              <div className="md:w-2/3">
                <div className="bg-white border border-gray-200 rounded-lg p-6 lg:p-8 sticky top-32">
                  {/* El contenido se carga dinámicamente */}
                  {(() => {
                    const active = solutionsData[activeSolution];
                    return (
                      <div>
                        <div className="flex items-center gap-3 mb-4">
                          <active.icon className="text-blue-600" size={24} />
                          <h3 className="font-semibold text-xl text-[#141414]">{active.title}</h3>
                        </div>
                        <div className="text-sm text-gray-700">
                          <p className="mb-3 text-base">
                            <span className="font-semibold text-red-600">{t('backup.solutions.problem_label')}</span> {active.problem}
                          </p>
                          <p className="font-semibold text-blue-700 text-base">{t('backup.solutions.solution_label')}</p>
                          <p className="mb-4 text-base">{active.solution}</p>
                          
                          <p className="font-semibold text-gray-800 text-base mb-2">{t('backup.solutions.benefits_label')}</p>
                          <ul className="space-y-1.5 list-disc list-outside pl-5">
                            {active.benefits.map((benefit, index) => (
                              <li key={index} className="text-gray-600">{benefit}</li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    );
                  })()}
                </div>
              </div>

            </div>
          </section>


          {/* Sección 3: Estrategia On-Premise vs Híbrida */}
          <section className="mb-24">
            <h2 className="text-3xl font-bold text-center text-[#141414] mb-12">{t('backup.strategy.title')}</h2>
            
            <div className="space-y-12">
              {/* Estrategia 1: Control Local */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
                {/* Visual */}
                <div className="bg-gray-50 border border-gray-200 rounded-xl p-8 shadow-md h-full flex items-center justify-center">
                  <div className="text-center w-full">
                    <div className="inline-block p-3 bg-white border border-gray-200 rounded-full mb-3">
                      <Server size={24} className="text-gray-800" />
                    </div>
                    <h5 className="font-bold text-gray-800 mb-4">{t('backup.strategy.local.server')}</h5>
                    <div className="flex items-center justify-center space-x-4">
                      <div className="text-center p-4 rounded-lg bg-white border border-gray-200 min-w-[140px]">
                        <HardDrive size={20} className="mx-auto text-gray-700" />
                        <p className="font-semibold text-gray-800 text-sm mt-1">{t('backup.strategy.local.data')}</p>
                      </div>
                      <span className="text-2xl font-light text-gray-400">+</span>
                      <div className="text-center p-4 rounded-lg bg-white border border-gray-200 min-w-[140px]">
                        <Archive size={20} className="mx-auto text-blue-700" />
                        <p className="font-semibold text-blue-700 text-sm mt-1">{t('backup.strategy.local.backup')}</p>
                      </div>
                    </div>
                  </div>
                </div>
                {/* Texto */}
                <div>
                  <h3 className="text-2xl font-semibold text-[#141414] mb-3">{t('backup.strategy.local.title')}</h3>
                  <p className="text-gray-700 mb-4">
                    {t('backup.strategy.local.desc')}
                  </p>
                  <ul className="list-disc list-outside space-y-2 pl-5 text-gray-700">
                    {t('backup.strategy.local.points', { returnObjects: true }).map((point, index) => (
                      <li key={index}>{point}</li>
                    ))}
                  </ul>
                </div>
              </div>

              {/* Estrategia 2: Opción Híbrida */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
                {/* Texto */}
                <div className="md:order-last">
                  <h3 className="text-2xl font-semibold text-[#141414] mb-3">{t('backup.strategy.hybrid.title')}</h3>
                  <p className="text-gray-700 mb-4">
                    {t('backup.strategy.hybrid.desc')}
                  </p>
                   <ul className="list-disc list-outside space-y-2 pl-5 text-gray-700">
                    {t('backup.strategy.hybrid.points', { returnObjects: true }).map((point, index) => (
                      <li key={index}>{point}</li>
                    ))}
                  </ul>
                </div>
                {/* Visual */}
                <div className="bg-blue-50 border border-blue-200 rounded-xl p-8 shadow-md h-full flex items-center justify-center">
                   <div className="flex items-center justify-center space-x-4 w-full">
                    <div className="text-center p-4 rounded-lg bg-white border border-gray-200 min-w-[140px]">
                      <Server size={20} className="mx-auto text-gray-700" />
                      <p className="font-bold text-gray-800 text-sm mt-1">{t('backup.strategy.hybrid.local')}</p>
                    </div>
                    <ArrowRight size={24} className="text-blue-500 flex-shrink-0" />
                    <div className="text-center p-4 rounded-lg bg-white border border-gray-200 min-w-[140px]">
                      <Cloud size={20} className="mx-auto text-blue-700" />
                      <p className="font-bold text-blue-700 text-sm mt-1">{t('backup.strategy.hybrid.cloud')}</p>
                    </div>
                  </div>
                </div>
              </div>

            </div>
          </section>

          {/* Sección 4: Implementación y Licenciamiento */}
          <section className="mb-20">
             <h2 className="text-3xl font-bold text-center text-[#141414] mb-12">{t('backup.implementation.title')}</h2>
            <div className="grid md:grid-cols-2 gap-8 md:gap-12 items-start">
              
              {/* Columna 1: Cómo Trabajamos */}
              <div>
                <h3 className="text-2xl font-semibold text-[#141414] mb-6">{t('backup.implementation.process_title')}</h3>
                <div className="space-y-6">
                  {implementationSteps.map((step, index) => (
                    <StepItem
                      key={index}
                      icon={[FileSearch, Settings2, Wrench, ShieldCheck][index]}
                      title={step.title}
                      text={step.text}
                    />
                  ))}
                </div>
              </div>
              
              {/* Columna 2: Modelos de Licenciamiento */}
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-6">
                <h3 className="text-2xl font-semibold text-[#141414] mb-6">{t('backup.implementation.license_title')}</h3>
                <p className="text-gray-700 mb-6">{t('backup.implementation.license_desc')}</p>
                <div className="space-y-5">
                  {licenseItems.map((item, index) => (
                    <StepItem
                      key={index}
                      icon={[DollarSign, Users, CheckSquare][index]}
                      title={item.title}
                      text={item.text}
                    />
                  ))}
                </div>
              </div>
            </div>
          </section>

          {/* --- FIN NUEVO DISEÑO --- */}

        </div>

        {/* Sección 5: Cierre y Conversión */}
        <section className="max-w-4xl mx-auto mt-24 mb-12">
          <div className="bg-gray-50 border border-gray-200 rounded-xl p-8 md:p-12 text-center">
            <h2 className="text-3xl font-bold text-[#141414] mb-4">
              {t('backup.final.title')}
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto mb-8">
              {t('backup.final.desc')}
            </p>
            {/* Botón para abrir el popup */}
            <button
              onClick={openPopup}
              className="inline-flex items-center justify-center gap-2 bg-[#141414] text-white px-8 py-3 rounded-lg hover:bg-gray-800 transition font-semibold"
            >
              <ShieldAlert size={18} />
              {t('backup.final.btn')}
            </button>
             <p className="text-sm text-gray-500 max-w-xl mx-auto mt-4">
              {t('backup.final.note')}
            </p>
          </div>
        </section>

      </main>
    </div>
  );
};

export default SistemasBackupPage;

